/**
 * Shared data hooks over the Dayvise API (prompt.md §37, §40, §47).
 *
 * WHY these live in one module rather than per screen: web and desktop must
 * behave identically for the same action — the same invalidation after sending a
 * message, the same optimistic pause, the same cache key. A screen that rolled
 * its own mutation would drift from the other client.
 *
 * Every hook is workspace scoped through `queryKeys`, so a switch cannot leak a
 * cached row between workspaces.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ConversationId, McpServer, MembershipRole, RunId, ScheduleId } from "@dayvise/domain";

import { queryKeys, type QueryScope } from "./queryKeys.ts";
import { useActiveWorkspaceId, useDayviseClient, useSession } from "./providers.tsx";

export function useScope(): QueryScope {
  return { workspaceId: useActiveWorkspaceId() };
}

/* ------------------------------- accounts -------------------------------- */

export function useWorkspaceTools() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({ queryKey: queryKeys.tools(scope), queryFn: () => client.tools.list(), enabled: scope.workspaceId !== null });
}

export function useToolPolicyMutation() {
  const client = useDayviseClient();
  const scope = useScope();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async ({ toolId, effect }: { toolId: string; effect: "allow" | "deny" | "require_approval" | null }) => {
      if (effect === null) await client.admin.removeToolPolicy(toolId);
      else await client.admin.setToolPolicy({ toolId, effect });
    },
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: queryKeys.tools(scope) });
      await cache.invalidateQueries({ queryKey: queryKeys.audit(scope) });
    },
  });
}

export function useAgents() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.agents(scope),
    queryFn: () => client.agents.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useCreateAgent() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof client.agents.create>[0]) => client.agents.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents(scope) });
    },
  });
}

export function useAgentMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.agents(scope) });
  };
  return {
    create: useMutation({ mutationFn: (input: Parameters<typeof client.agents.create>[0]) => client.agents.create(input), onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ agentId, ...input }: { agentId: string } & Parameters<typeof client.agents.update>[1]) =>
        client.agents.update(agentId, input),
      onSuccess: refresh,
    }),
    share: useMutation({ mutationFn: (agentId: string) => client.agents.share(agentId), onSuccess: refresh }),
    unshare: useMutation({ mutationFn: (agentId: string) => client.agents.unshare(agentId), onSuccess: refresh }),
    delete: useMutation({ mutationFn: (agentId: string) => client.agents.delete(agentId), onSuccess: refresh }),
    archive: useMutation({ mutationFn: (agentId: string) => client.agents.delete(agentId), onSuccess: refresh }),
  };
}

/* ----------------------------- conversations ----------------------------- */

export function useConversations() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.conversations(scope),
    queryFn: () => client.conversations.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useConversation(conversationId: ConversationId | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.conversation(scope, conversationId ?? ("none" as ConversationId)),
    queryFn: () => client.conversations.get(conversationId as ConversationId),
    refetchInterval: 5_000,
    enabled: scope.workspaceId !== null && conversationId !== null,
  });
}

export function useMessages(conversationId: ConversationId | null, branchId: string | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.messages(scope, conversationId ?? ("none" as ConversationId), branchId),
    queryFn: () => client.conversations.messages(conversationId as ConversationId, branchId ? { branchId } : undefined),
    enabled: scope.workspaceId !== null && conversationId !== null,
  });
}

export function useCreateConversation() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof client.conversations.create>[0]) => client.conversations.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations(scope) });
    },
  });
}

/**
 * Interactive conversation deletion (prompt.md §8.4).
 *
 * The related-memory policy is part of the call, not a default: the server
 * refuses a delete that does not state it, so the dialog that collects the
 * choice and this mutation are the only path to a delete.
 */
export function useDeleteConversation() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { conversationId: ConversationId; relatedMemories: "keep" | "delete" }) =>
      client.conversations.delete(input.conversationId, { relatedMemories: input.relatedMemories }),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations(scope) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(scope, variables.conversationId) });
    },
  });
}

/**
 * Sending a message returns a run, not an answer (prompt.md §48) — the reply
 * arrives on the run event stream. So the mutation invalidates the transcript
 * and lets the caller subscribe to the run it returns.
 */
export function useSendMessage(conversationId: ConversationId | null) {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      text: string;
      interpretSlash?: boolean;
      signal?: AbortSignal;
      branchId?: string;
      workingDirectory?: string;
      idempotencyKey?: string;
      secrets?: readonly { readonly label: string; readonly value: string }[];
      /** Exact accepted file revisions that ride with this message. */
      attachments?: readonly { readonly fileId: string; readonly version: number; readonly name?: string }[];
    }) => {
      if (conversationId === null) throw new Error("no conversation selected");
      return client.conversations.send(conversationId, input, { signal: input.signal });
    },
    onSuccess: (_result, input) => {
      if (_result.result?.command?.name === "save" || _result.result?.command?.name === "export") void queryClient.invalidateQueries({ queryKey: queryKeys.files(scope) });
      if (_result.result?.command?.name === "import") void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(scope.workspaceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations(scope) });
      // A model command can change the agent default as well as branch state.
      if (/^\/(?:model|personality)(?:\s|$)/i.test(input.text.trim())) void queryClient.invalidateQueries({ queryKey: queryKeys.agents(scope) });
      if (conversationId !== null) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(scope, conversationId) });
      }
    },
  });
}

/* ---------------------------------- runs --------------------------------- */

export function useRuns() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.runs(scope),
    queryFn: () => client.runs.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useRun(runId: RunId | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.run(scope, runId ?? ("none" as RunId)),
    queryFn: () => client.runs.get(runId as RunId),
    enabled: scope.workspaceId !== null && runId !== null,
    // A live run changes while a client watches it, so this is the one query
    // that re-reads on an interval; everything else is invalidated by mutation.
    refetchInterval: (query) => {
      const state = query.state.data?.run.state;
      return state === undefined || ["completed", "cancelled", "failed_terminal"].includes(state) ? false : 5_000;
    },
  });
}

export function useRunApprovals(runId: RunId | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.runApprovals(scope, runId ?? ("none" as RunId)),
    queryFn: () => client.runs.approvals(runId as RunId),
    enabled: scope.workspaceId !== null && runId !== null,
  });
}

export function useRunDiff(runId: RunId | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.runDiff(scope, runId ?? ("none" as RunId)),
    queryFn: () => client.runs.diff(runId as RunId),
    enabled: scope.workspaceId !== null && runId !== null,
  });
}

/** Steering, pausing, resuming and cancelling all invalidate the same run. */
function useRunAction<TInput>(runId: RunId | null, action: (runId: RunId, input: TInput) => Promise<unknown>) {
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => {
      if (runId === null) throw new Error("no run selected");
      return action(runId, input);
    },
    onSuccess: () => {
      if (runId === null) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.run(scope, runId) });
    },
  });
}

export function useSteerRun(runId: RunId | null) {
  const client = useDayviseClient();
  return useRunAction(runId, (id, message: string) => client.runs.steer(id, message));
}

export function usePauseRun(runId: RunId | null) {
  const client = useDayviseClient();
  return useRunAction(runId, (id, reason: string | undefined) => client.runs.pause(id, reason));
}

export function useResumeRun(runId: RunId | null) {
  const client = useDayviseClient();
  return useRunAction(runId, (id, checkpointId: string | null | undefined) => client.runs.resume(id, checkpointId ?? null));
}

export function useCancelRun(runId: RunId | null) {
  const client = useDayviseClient();
  return useRunAction(runId, (id, reason: string | undefined) => client.runs.cancel(id, reason));
}

/* -------------------------------- schedules ------------------------------ */

export function useSchedules() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.schedules(scope),
    queryFn: () => client.schedules.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useScheduleMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.schedules(scope) });
  };
  return {
    create: useMutation({ mutationFn: (input: Parameters<typeof client.schedules.create>[0]) => client.schedules.create(input), onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ scheduleId, ...input }: { scheduleId: ScheduleId } & Parameters<typeof client.schedules.update>[1]) =>
        client.schedules.update(scheduleId, input),
      onSuccess: refresh,
    }),
    trigger: useMutation({ mutationFn: (scheduleId: ScheduleId) => client.schedules.trigger(scheduleId), onSuccess: refresh }),
    enable: useMutation({ mutationFn: (scheduleId: ScheduleId) => client.schedules.enable(scheduleId), onSuccess: refresh }),
    disable: useMutation({ mutationFn: (scheduleId: ScheduleId) => client.schedules.disable(scheduleId), onSuccess: refresh }),
    remove: useMutation({ mutationFn: (scheduleId: ScheduleId) => client.schedules.remove(scheduleId), onSuccess: refresh }),
  };
}

/* --------------------------------- memory -------------------------------- */

export function useMemory() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.memory(scope),
    queryFn: () => client.memory.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useMemoryMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.memory(scope) });
  };
  return {
    create: useMutation({ mutationFn: (input: Parameters<typeof client.memory.create>[0]) => client.memory.create(input), onSuccess: refresh }),
    confirm: useMutation({ mutationFn: (input: { memoryId: string; version: number; content?: string }) => client.memory.confirm(input.memoryId, input), onSuccess: refresh }),
    /** Promotion is only ever reachable from an explicit user action (§8.2). */
    promote: useMutation({ mutationFn: (memoryId: string) => client.memory.promote(memoryId), onSuccess: refresh }),
    remove: useMutation({ mutationFn: (memoryId: string) => client.memory.remove(memoryId), onSuccess: refresh }),
  };
}

/* ---------------------------------- files -------------------------------- */

export function useFiles(prefix?: string) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.files(scope, prefix),
    queryFn: () => client.files.list(prefix ? { prefix } : undefined),
    enabled: scope.workspaceId !== null,
  });
}

/* ------------------------------- credentials ----------------------------- */

export function useCredentials() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.credentials(scope),
    queryFn: () => client.credentials.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useCredentialMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.credentials(scope) });
  };
  return {
    create: useMutation({ mutationFn: (input: Parameters<typeof client.credentials.create>[0]) => client.credentials.create(input), onSuccess: refresh }),
    test: useMutation({ mutationFn: (credentialId: string) => client.credentials.test(credentialId), onSuccess: refresh }),
    revoke: useMutation({ mutationFn: (credentialId: string) => client.credentials.revoke(credentialId), onSuccess: refresh }),
  };
}

/* --------------------------- usage & entitlements ------------------------ */

export function useUsage() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.usage(scope),
    queryFn: () => client.usage.get(),
    enabled: scope.workspaceId !== null,
  });
}

export function useRefinements() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.refinements(scope),
    queryFn: () => client.learning.refinements.list({ limit: 50 }),
    enabled: scope.workspaceId !== null,
  });
}

/** Applying or reverting a refinement writes the target, so both caches are invalidated. */
export function useRefinementMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.refinements(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.memory(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.skills(scope) });
  };
  return {
    create: useMutation({
      mutationFn: (input: Parameters<typeof client.learning.refinements.create>[0]) => client.learning.refinements.create(input),
      onSuccess: invalidate,
    }),
    apply: useMutation({
      mutationFn: (refinementId: string) => client.learning.refinements.apply(refinementId),
      onSuccess: invalidate,
    }),
    revert: useMutation({
      mutationFn: (refinementId: string) => client.learning.refinements.revert(refinementId),
      onSuccess: invalidate,
    }),
  };
}

/* --------------------------------- billing -------------------------------- */

export function useBilling() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.billing(scope),
    queryFn: () => client.billing.get(),
    enabled: scope.workspaceId !== null,
  });
}

/**
 * Starting a checkout is a mutation, and it deliberately does NOT invalidate the billing cache on
 * success. A subscription is not active until the payment provider's webhook says so, so a UI that
 * refreshed as though it were would show a plan the workspace has not been granted — the same
 * mistake as reading a missing record as a permissive one, one layer up.
 */
export function useBillingMutations() {
  const client = useDayviseClient();
  return {
    checkout: useMutation({ mutationFn: (plan: string) => client.billing.checkout({ plan }) }),
  };
}

export function useEntitlements() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.entitlements(scope),
    queryFn: () => client.entitlements.get(),
    enabled: scope.workspaceId !== null,
  });
}

export function useNotifications() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.notifications(scope),
    queryFn: () => client.notifications.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useAuditLog(enabled: boolean) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.audit(scope),
    queryFn: () => client.audit.list(),
    enabled: scope.workspaceId !== null && enabled,
  });
}

/* ---------------------------------- yolo --------------------------------- */

export function useYoloMode() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.yolo(scope),
    queryFn: () => client.yolo.get(),
    enabled: scope.workspaceId !== null,
  });
}

export function useYoloModeMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return {
    update: useMutation({
      mutationFn: (input: Parameters<typeof client.yolo.update>[0]) => client.yolo.update(input),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.yolo(scope) });
      },
    }),
  };
}

/* --------------------------------- boards -------------------------------- */


export function useBoards(includeArchived = false) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: [...queryKeys.boards(scope), includeArchived ? "lifecycle" : "visible"] as const,
    queryFn: () => client.boards.list(includeArchived ? { includeArchived: true } : undefined),
    enabled: scope.workspaceId !== null,
  });
}

export function useBoard(boardId: string | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.board(scope, boardId ?? "none"),
    queryFn: () => client.boards.get(boardId as string),
    enabled: scope.workspaceId !== null && boardId !== null,
  });
}

export function useBoardMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = (boardId?: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.boards(scope) });
    if (boardId !== undefined) void queryClient.invalidateQueries({ queryKey: queryKeys.board(scope, boardId) });
  };
  return {
    create: useMutation({
      mutationFn: (input: Parameters<typeof client.boards.create>[0]) => client.boards.create(input),
      onSuccess: () => refresh(),
    }),
    archive: useMutation({
      mutationFn: ({ boardId, restore }: { boardId: string; restore?: boolean }) => (restore === true ? client.boards.restore(boardId) : client.boards.archive(boardId)),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    addColumn: useMutation({
      mutationFn: ({ boardId, ...input }: { boardId: string } & Parameters<typeof client.boards.addColumn>[1]) => client.boards.addColumn(boardId, input),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    addTask: useMutation({
      mutationFn: ({ boardId, ...input }: { boardId: string } & Parameters<typeof client.boards.addTask>[1]) => client.boards.addTask(boardId, input),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    updateTask: useMutation({
      mutationFn: ({ boardId, taskId, ...input }: { boardId: string; taskId: string } & Parameters<typeof client.boards.updateTask>[2]) => client.boards.updateTask(boardId, taskId, input),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    moveTask: useMutation({
      mutationFn: ({ boardId, taskId, ...input }: { boardId: string; taskId: string } & Parameters<typeof client.boards.moveTask>[2]) => client.boards.moveTask(boardId, taskId, input),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    /** One agent run for one ready task; the platform refuses a duplicate dispatch. */
    dispatch: useMutation({
      mutationFn: ({ boardId, taskId, agentId }: { boardId: string; taskId: string; agentId?: string }) =>
        client.boards.dispatch(boardId, taskId, agentId === undefined ? {} : { agentId }),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    cancelTask: useMutation({
      mutationFn: ({ boardId, taskId, reason }: { boardId: string; taskId: string; reason?: string }) =>
        client.boards.cancelTask(boardId, taskId, reason === undefined ? {} : { reason }),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
    settleDispatches: useMutation({
      mutationFn: (boardId: string) => client.boards.settleDispatches(boardId),
      onSuccess: (_result, boardId) => refresh(boardId),
    }),
    postMessage: useMutation({
      mutationFn: ({ boardId, taskId, body, kind }: { boardId: string; taskId: string; body: string; kind?: Parameters<typeof client.boards.postMessage>[2]["kind"] }) =>
        client.boards.postMessage(boardId, taskId, kind === undefined ? { body } : { body, kind }),
      onSuccess: (_result, input) => refresh(input.boardId),
    }),
  };
}

/* --------------------------- presence and calls --------------------------- */


export function usePresenceLeases() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.presence(scope),
    queryFn: () => client.presence.list(),
    enabled: scope.workspaceId !== null,
  });
}

/** Exactly the record presence routing reads, so the panel cannot drift from policy. */
export function useCallPreferences() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.callPreferences(scope),
    queryFn: () => client.callPreferences.get(),
    enabled: scope.workspaceId !== null,
  });
}

export function useCallPreferenceMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.callPreferences(scope) });
  };
  return {
    update: useMutation({
      mutationFn: (input: Parameters<typeof client.callPreferences.update>[0]) => client.callPreferences.update(input),
      onSuccess: refresh,
    }),
    reset: useMutation({
      mutationFn: () => client.callPreferences.reset(),
      onSuccess: refresh,
    }),
  };
}

/* ----------------------------------- mcp --------------------------------- */

export function useMcpServers() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.mcpServers(scope),
    queryFn: () => client.mcp.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useMcpServerTools(serverId: string | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.mcpTools(scope, serverId ?? "none"),
    queryFn: () => client.mcp.tools(serverId as string),
    enabled: scope.workspaceId !== null && serverId !== null,
  });
}

export function useMcpServerMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers(scope) });
  };
  return {
    create: useMutation({
      mutationFn: (input: Parameters<typeof client.mcp.create>[0]) => client.mcp.create(input),
      onSuccess: refresh,
    }),
    test: useMutation({
      mutationFn: (serverId: string) => client.mcp.test(serverId),
      onSuccess: refresh,
    }),
    /**
     * The on/off switch. A disabled server keeps its configuration and credential
     * but is no longer offered to a run, which is what "off" must mean. The
     * response carries the stored server, so the catalogue is not re-read for a
     * toggle; "Reload catalogue" remains the explicit re-read.
     */
    setEnabled: useMutation({
      mutationFn: ({ serverId, enabled }: { serverId: string; enabled: boolean }) => client.mcp.update(serverId, { enabled }),
      onSuccess: (result) => {
        queryClient.setQueryData<{ servers: readonly McpServer[] }>(queryKeys.mcpServers(scope), (previous) =>
          previous === undefined
            ? previous
            : { ...previous, servers: previous.servers.map((server) => (server.mcpServerId === result.server.mcpServerId ? result.server : server)) });
      },
    }),
    remove: useMutation({
      mutationFn: (serverId: string) => client.mcp.remove(serverId),
      onSuccess: refresh,
    }),
  };
}

/* --------------------------------- skills -------------------------------- */

export function useSkills(includeArchived = false) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: [...queryKeys.skills(scope), includeArchived ? "lifecycle" : "visible"] as const,
    queryFn: () => client.skills.list(includeArchived ? { includeArchived: true } : undefined),
    enabled: scope.workspaceId !== null,
  });
}

export function useSkillDocument(skillId: string | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: [...queryKeys.skill(scope, skillId ?? "none"), "document"] as const,
    queryFn: () => client.skills.document(skillId as string),
    enabled: scope.workspaceId !== null && skillId !== null,
  });
}

export function useSkillMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.skills(scope) });
  };
  return {
    create: useMutation({
      mutationFn: (input: Parameters<typeof client.skills.create>[0]) => client.skills.create(input),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ skillId, ...input }: { skillId: string } & Parameters<typeof client.skills.update>[1]) =>
        client.skills.update(skillId, input),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (skillId: string) => client.skills.remove(skillId),
      onSuccess: refresh,
    }),
    /** Turning a skill back on. A restore never grants it to a run by itself. */
    restore: useMutation({
      mutationFn: (skillId: string) => client.skills.restore(skillId),
      onSuccess: refresh,
    }),
  };
}

export function useSkillShareTargets() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({ queryKey: queryKeys.skillShareTargets(scope), queryFn: () => client.skillShares.targets(), enabled: scope.workspaceId !== null });
}

export function useOwnedSkillShares() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({ queryKey: [...queryKeys.skillShares(scope), "owned"], queryFn: () => client.skillShares.owned(), enabled: scope.workspaceId !== null });
}

export function useIncomingSkillShares() {
  const client = useDayviseClient();
  const scope = useScope();
  const workspace = useSession().session.workspace;
  const canReview = workspace?.kind !== "personal" && (workspace?.role === "owner" || workspace?.role === "admin");
  return useQuery({ queryKey: [...queryKeys.skillShares(scope), "incoming"], queryFn: () => client.skillShares.incoming(), enabled: scope.workspaceId !== null && canReview });
}

export function useSkillShareMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.skillShares(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.skillShareTargets(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.skills(scope) });
  };
  return {
    share: useMutation({ mutationFn: ({ skillId, targetWorkspaceId }: { skillId: string; targetWorkspaceId: string }) => client.skills.share(skillId, { targetWorkspaceId }), onSuccess: refresh }),
    unshare: useMutation({ mutationFn: ({ skillId, shareId }: { skillId: string; shareId: string }) => client.skills.unshare(skillId, { shareId }), onSuccess: refresh }),
    accept: useMutation({ mutationFn: ({ shareId, followUpdates }: { shareId: string; followUpdates: boolean }) => client.skillShares.accept(shareId, { followUpdates }), onSuccess: refresh }),
    update: useMutation({ mutationFn: ({ shareId, expectedTargetVersion, followUpdates }: { shareId: string; expectedTargetVersion: string | null; followUpdates: boolean }) => client.skillShares.update(shareId, { expectedTargetVersion, followUpdates }), onSuccess: refresh }),
    revoke: useMutation({ mutationFn: (shareId: string) => client.skillShares.revoke(shareId), onSuccess: refresh }),
  };
}

/* ---------------------------------- admin --------------------------------- */

export function useAdminMembers(enabled: boolean) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.adminMembers(scope),
    queryFn: () => client.admin.members(),
    enabled: scope.workspaceId !== null && enabled,
  });
}

export function useAdminSharedMemories(enabled: boolean) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.adminSharedMemories(scope),
    queryFn: () => client.admin.sharedMemories(),
    enabled: scope.workspaceId !== null && enabled,
  });
}

export function useAdminMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refreshMembers = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminMembers(scope) });
  };
  const refreshMemories = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminSharedMemories(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.memory(scope) });
  };
  return {
    addMember: useMutation({
      mutationFn: (input: { userId: string; role: MembershipRole }) => client.admin.addMember(input),
      onSuccess: refreshMembers,
    }),
    removeMember: useMutation({
      mutationFn: (userId: string) => client.admin.removeMember(userId),
      onSuccess: refreshMembers,
    }),
    runRetention: useMutation({
      mutationFn: () => client.admin.runRetention(),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.conversations(scope) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.files(scope) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.audit(scope) });
      },
    }),
    removeSharedMemory: useMutation({
      mutationFn: (memoryId: string) => client.admin.removeSharedMemory(memoryId),
      onSuccess: refreshMemories,
    }),
  };
}

export function useStorageRoute() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.storageRoute(scope),
    queryFn: () => client.storage.getRoute(),
    enabled: scope.workspaceId !== null,
  });
}

export function useStorageMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return {
    setRoute: useMutation({
      mutationFn: (input: {
        structured?: "d1_shared" | "postgres_byo";
        blob?: "r2_managed" | "s3_byo";
        structuredCredentialId?: string | null;
        blobCredentialId?: string | null;
        config?: unknown;
      }) => client.storage.setRoute(input),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.storageRoute(scope) });
      },
    }),
    startMigration: useMutation({
      mutationFn: (input: {
        direction: "d1_to_postgres" | "postgres_to_d1" | "r2_to_s3" | "s3_to_r2";
        targetCredentialId?: string;
        targetConfig?: unknown;
      }) => client.storage.startMigration(input),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.storageRoute(scope) });
      },
    }),
  };
}

export function useStorageMigrationJob(jobId: string | null) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.storageMigrationJob(scope, jobId ?? "none"),
    queryFn: () => (jobId ? client.storage.getMigrationJob(jobId) : null),
    enabled: scope.workspaceId !== null && jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.job?.status;
      return status === "pending" || status === "running" ? 1000 : false;
    },
  });
}



export function useSnapshots() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.snapshots(scope),
    queryFn: () => client.snapshots.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useSnapshotMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.snapshots(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.skills(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminMembers(scope) });
  };
  return {
    create: useMutation({
      mutationFn: () => client.snapshots.create(),
      onSuccess: refresh,
    }),
    restore: useMutation({
      mutationFn: (snapshotId: string) => client.snapshots.restore(snapshotId, { confirm: true }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (snapshotId: string) => client.snapshots.remove(snapshotId),
      onSuccess: refresh,
    }),
  };
}

export function useAutomationTemplates() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.automationTemplates(scope),
    queryFn: () => client.automationTemplates.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useAutomationTemplateMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.automationTemplates(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.schedules(scope) });
  };
  return {
    create: useMutation({
      mutationFn: (input: Parameters<typeof client.automationTemplates.create>[0]) => client.automationTemplates.create(input),
      onSuccess: refresh,
    }),
    use: useMutation({
      mutationFn: ({ templateId, agentId, parameters, title }: { templateId: string; agentId: string; parameters?: Record<string, unknown>; title?: string }) =>
        client.automationTemplates.use(templateId, { agentId, parameters, title }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (templateId: string) => client.automationTemplates.remove(templateId),
      onSuccess: refresh,
    }),
  };
}

export function useSkillBundles() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.skillBundles(scope),
    queryFn: () => client.bundles.list(),
    enabled: scope.workspaceId !== null,
  });
}

export function useSkillBundleMutations() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.skillBundles(scope) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.skills(scope) });
  };
  return {
    create: useMutation({
      mutationFn: (input: { name: string; skillNames: readonly string[] }) => client.bundles.create(input),
      onSuccess: refresh,
    }),
    preview: useMutation({
      mutationFn: (bundleId: string) => client.bundles.get(bundleId),
    }),
    install: useMutation({
      mutationFn: ({ bundleId, expectedVersions }: { bundleId: string; expectedVersions: Readonly<Record<string, string | null>> }) =>
        client.bundles.install(bundleId, { confirm: true, expectedVersions }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (bundleId: string) => client.bundles.remove(bundleId),
      onSuccess: refresh,
    }),
  };
}

export function useLearningRefinements() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: [...queryKeys.learningEvents(scope), "refinements"] as const,
    queryFn: () => client.learning.refinements.list({}),
    enabled: scope.workspaceId !== null,
  });
}

export function useOutputRestorePreview(
  conversationId: string | null,
  snapshotFileId: string | null,
  branchId: string | null,
) {
  const client = useDayviseClient();
  return useQuery({
    queryKey: [
      "outputRestorePreview",
      conversationId ?? "none",
      snapshotFileId ?? "none",
      branchId ?? "default",
    ] as const,
    queryFn: () => client.conversations.outputPreview(conversationId as never, snapshotFileId as string, branchId ? { branchId } : undefined),
    enabled: conversationId !== null && snapshotFileId !== null,
  });
}

export function useRestoreOutput() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      snapshotFileId,
      expectedVersion,
      branchId,
    }: {
      conversationId: string;
      snapshotFileId: string;
      expectedVersion?: number;
      branchId?: string;
    }) =>
      client.conversations.restoreOutput(conversationId, snapshotFileId, {
        confirm: true,
        ...(expectedVersion === undefined ? {} : { expectedVersion }),
        ...(branchId === undefined ? {} : { branchId }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.files(scope) });
      void queryClient.invalidateQueries({ queryKey: ["conversationOutputs"] });
    },
  });
}

export function useLearningEvents(limit = 50) {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: [...queryKeys.learningEvents(scope), limit] as const,
    queryFn: () => client.learning.events.list({ limit }),
    enabled: scope.workspaceId !== null,
  });
}

export function useHome() {
  const client = useDayviseClient();
  const scope = useScope();
  return useQuery({
    queryKey: queryKeys.home(scope),
    queryFn: () => client.home.get(),
    enabled: scope.workspaceId !== null,
  });
}

export function useHomeMutation() {
  const client = useDayviseClient();
  const scope = useScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaultWorkspaceId: string | null) => client.home.set(defaultWorkspaceId),
    onSuccess: (resolution) => {
      queryClient.setQueryData(queryKeys.home(scope), resolution);
    },
  });
}
